import { FC } from 'react';
import { Button } from 'components/common';
import { FormattedMessage } from 'react-intl';
import { Address } from './Address';
import { useSearchParams } from 'react-router-dom';
import { useNavigateBack } from 'hooks';
import { ApplicationFormPageEnum } from '../types';
import { addressHistoryQuestions } from './questions';
import { IAddressHistoryProps } from './types';
import { IAddressDetails } from '../AddAddress/types';
import dayjs from 'dayjs';

const MINIMUM_YEARS_OF_LIVING = 3;

export const AddressHistory: FC<IAddressHistoryProps> = ({
  addressHistory,
  onRemoveAddressById,
  onAddAddressRedirect,
  onEditAddressRedirect,
}) => {
  const navigateBack = useNavigateBack();
  const [searchParams, setSearchParams] = useSearchParams();
  const formApplicantsNumber = searchParams.get('applicantNames')?.split(',').length || 0;
  const activeApplicant = parseInt(searchParams.get('activeApplicant') || '');

  const addressToString = (address: IAddressDetails) => {
    const { address1, address2, fullAddress, city, postcode, country } = address;
    if (fullAddress?.trim()?.length) {
      return fullAddress.trim();
    }
    return [address1, address2, city, postcode, country].filter(part => part?.trim()?.length).join(', ');
  };

  const dateToString = (address: IAddressDetails) => `${address.month} ${address.year}`;

  const nextStepRedirectHandler = () => {
    const isLastApplicant = activeApplicant === formApplicantsNumber;
    const newActiveAppicant = isLastApplicant ? 1 : activeApplicant + 1;
    const nextPage = isLastApplicant
      ? ApplicationFormPageEnum.INTRODUCTION
      : ApplicationFormPageEnum.PARTNER_ADDRESS_HISTORY;

    searchParams.set('activeApplicant', `${newActiveAppicant}`);
    searchParams.set('section', nextPage);
    setSearchParams(searchParams);
  };

  const oldestAddress = addressHistory.sort((a, b) => {
    const date1 = dayjs(`1 ${a.month} ${a.year}`);
    const date2 = dayjs(`1 ${b.month} ${b.year}`);

    return date1.diff(date2);
  })[0];

  const today = dayjs();
  const oldestAddressDate = oldestAddress && dayjs(`1 ${oldestAddress.month} ${oldestAddress.year}`);
  const isOldEnoughAddress = today.diff(oldestAddressDate, 'year') >= MINIMUM_YEARS_OF_LIVING;

  return (
    <>
      <div className={'mb-8 flex items-baseline justify-between'}>
        <FormattedMessage id={addressHistoryQuestions.addressHistory.header} tagName={'p'} />
        <Button variant={'contained'} className={'ml-5 shrink-0'} onClick={onAddAddressRedirect}>
          <FormattedMessage id={addressHistoryQuestions.addressHistory.add} />
        </Button>
      </div>
      <div className={'w-full'}>
        {addressHistory.length === 0 ? (
          <p className={'rounded border border-solid border-gray-300 py-16 text-center text-gray-400'}>
            <FormattedMessage id={addressHistoryQuestions.addressHistory.empty} />
          </p>
        ) : (
          <div className={'grid grid-cols-4 gap-4'}>
            {addressHistory.map(address => (
              <Address
                key={address.id}
                onDelete={() => onRemoveAddressById(address.id)}
                onEdit={() => onEditAddressRedirect(address)}
                address={addressToString(address)}
                startDate={dateToString(address)}
              />
            ))}
          </div>
        )}
      </div>
      <div className={'mt-8 flex justify-between'}>
        <Button variant={'outlined'} onClick={navigateBack}>
          <FormattedMessage id={'cta.back'} />
        </Button>
        <Button variant={'contained'} onClick={nextStepRedirectHandler} disabled={!isOldEnoughAddress}>
          <FormattedMessage id={'cta.continue'} />
        </Button>
      </div>
    </>
  );
};
